import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import AppleSignInButton from "@/components/auth/AppleSignInButton";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Mail, Lock, ShieldCheck, KeyRound, ArrowLeft, Phone } from "lucide-react";

/** Extract a human-readable message from an axios error (supports FastAPI's list-of-issues `detail`). */
function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return err?.message || fallback;
}

export default function LoginPage() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const emailFromUrl = params.get("email") || "";

  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [creds, setCreds] = useState({ email: emailFromUrl, password: "" });
  const [challenge, setChallenge] = useState(null); // { ephemeralToken, channel, maskedDestination, ttlMinutes }
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const codeRef = useRef(null);

  // TTL countdown for the OTP challenge (TOTP codes self-refresh — no timer)
  useEffect(() => {
    if (!challenge || challenge.channel === "totp") return;
    setSecondsLeft(challenge.ttlMinutes * 60);
    const id = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [challenge]);

  useEffect(() => {
    if (step === "otp" && codeRef.current) codeRef.current.focus();
  }, [step]);

  // Iter-297 — surface a soft toast when the user cancels the Google
  // consent screen. The backend redirects to /login?google_error=cancelled
  // so we clear the param after showing the toast to avoid duplicate
  // fires on refresh.
  useEffect(() => {
    const err = params.get("google_error");
    if (!err) return;
    if (err === "cancelled") {
      toast("Google sign-in cancelled — no worries, try again anytime.");
    } else {
      toast.error("Google sign-in didn't complete — please try again.");
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("google_error");
      window.history.replaceState(null, "", url.pathname + url.search);
    } catch { /* noop */ }
  }, []);

  const onCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(creds.email.trim(), creds.password);
      if (r.kind === "ok") {
        toast.success("Welcome back!");
        nav(next, { replace: true });
        return;
      }
      // 2FA required — advance to OTP step
      setChallenge({
        ephemeralToken: r.ephemeralToken,
        channel: r.channel,
        maskedDestination: r.maskedDestination,
        ttlMinutes: r.ttlMinutes,
      });
      setStep("otp");
      if (r.channel === "totp") {
        toast.success("Enter the code from your authenticator app.");
      } else {
        toast.success(`We sent a code to ${r.maskedDestination}`);
      }
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const onOtpSubmit = async (e) => {
    e.preventDefault();
    if (!challenge?.ephemeralToken) return;
    if (code.trim().length < 4) { toast.error("Enter the code we sent you"); return; }
    setLoading(true);
    try {
      await verifyOtp(challenge.ephemeralToken, code.trim());
      toast.success("Signed in.");
      nav(next, { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Verification failed"));
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (!challenge?.ephemeralToken || resending) return;
    setResending(true);
    try {
      const r = await resendOtp(challenge.ephemeralToken);
      toast.success(`Code resent to ${r?.masked_destination || challenge.maskedDestination}`);
      setSecondsLeft(challenge.ttlMinutes * 60);
    } catch (err) {
      toast.error(errMsg(err, "Could not resend code"));
    } finally {
      setResending(false);
    }
  };

  const goBackToCredentials = () => {
    setStep("credentials");
    setCode("");
    setChallenge(null);
  };

  const mmss = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(secondsLeft % 60).padStart(2, "0")}`;
  const isTotp = challenge?.channel === "totp";
  const expired = !isTotp && secondsLeft === 0;

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-12">
      <Seo title="Login" />
      <div className="rounded-3xl bg-white shadow-card p-8">
        {step === "credentials" && (
          <>
            <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back</h1>
            <p className="mt-1 text-sm text-slate-500">Log in to post ads, chat, and manage your listings.</p>

            <form onSubmit={onCredentialsSubmit} className="mt-6 space-y-4" data-testid="login-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Email</span>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid={AUTH.emailInput}
                    value={creds.email}
                    onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                    placeholder="you@example.co.nz"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Password</span>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid={AUTH.passwordInput}
                    autoFocus={!!emailFromUrl}
                    value={creds.password}
                    onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                    type="password"
                    required
                    minLength={6}
                    autoComplete="current-password"
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                    placeholder="••••••••"
                  />
                </div>
                {/* Iter 720 — right-aligned reset entry point */}
                <span className="mt-1.5 flex justify-end">
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot-password-link"
                    className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
                  >
                    Forgot password?
                  </Link>
                </span>
              </label>
              <button
                data-testid={AUTH.submit}
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Log in
              </button>
            </form>

            {/* Iter-274d/e W5+W6: SSO row lives directly under the email
                form so it's the last thing the user sees before scrolling. */}
            <div className="mt-6 relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-soft" />
              </div>
              <div className="relative flex justify-center text-[10px] font-bold uppercase tracking-[0.2em]">
                <span className="bg-white px-3 text-slate-400">or continue with</span>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <GoogleSignInButton nextPath={next} />
              <AppleSignInButton nextPath={next} />
              <Link
                to={`/login/phone${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`}
                data-testid="phone-signin-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-800 font-semibold py-3 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Continue with phone number
              </Link>
            </div>

            <div className="mt-6 text-sm text-slate-500 text-center">
              New here?{" "}
              <Link data-testid={AUTH.switchBtn} to={`/register${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`} className="font-semibold text-[color:var(--brand-blue)] hover:underline">
                Create an account
              </Link>
            </div>
          </>
        )}

        {step === "otp" && challenge && (
          <>
            <button
              type="button"
              onClick={goBackToCredentials}
              data-testid="login-otp-back"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[color:var(--brand-blue)] mb-4 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Change email
            </button>

            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] mb-3">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Verify it&apos;s you</h1>
            {isTotp ? (
              <p className="mt-1 text-sm text-slate-500" data-testid="login-totp-copy">
                Enter the 6-digit code from your authenticator app — or one of your backup codes.
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-500">
                Enter the code we sent to <span className="font-semibold text-slate-800" data-testid="login-otp-masked">{challenge.maskedDestination}</span>.
              </p>
            )}

            <form onSubmit={onOtpSubmit} className="mt-6 space-y-4" data-testid="login-otp-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Verification code</span>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    ref={codeRef}
                    data-testid="login-otp-input"
                    value={code}
                    onChange={(e) =>
                      // Iter-363 — TOTP challenges accept a 6-digit app code
                      // OR an 8-char backup code (field relaxed to 12 chars).
                      setCode(
                        isTotp
                          ? e.target.value.replace(/[^0-9a-zA-Z]/g, "").slice(0, 12)
                          : e.target.value.replace(/[^0-9]/g, "").slice(0, 8)
                      )
                    }
                    inputMode={isTotp ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    required
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-lg tracking-[0.5em] text-center font-bold"
                    placeholder="••••••"
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px]">
                  {isTotp ? (
                    <span className="text-slate-400" data-testid="login-totp-refresh-note">
                      Codes refresh every 30 seconds
                    </span>
                  ) : (
                    <span className={expired ? "text-red-500 font-semibold" : "text-slate-400"} data-testid="login-otp-timer">
                      {expired ? "Code expired — request a new one" : `Expires in ${mmss}`}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={onResend}
                    disabled={resending}
                    data-testid={isTotp ? "login-totp-fallback" : "login-otp-resend"}
                    className="font-semibold text-[color:var(--brand-blue)] hover:underline disabled:opacity-60"
                  >
                    {resending ? "Sending…" : isTotp ? "Send me an SMS/email code instead" : "Resend code"}
                  </button>
                </div>
              </label>
              <button
                type="submit"
                disabled={loading || !code || expired}
                data-testid="login-otp-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-60 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Verify & log in
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
